import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { FiUploadCloud } from "react-icons/fi";
import { Input } from "../../../../../components/ui/input";
import { z } from "zod";
import { Button } from "../../../../../components/ui/button";
import { Textarea } from "../../../../../components/ui-comp/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  createScan,
  createTarget,
} from "../../../../../helpers/mutations/targets";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../../../components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../../../components/ui/form";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../../../../components/ui/radio-group";
import { Label } from "../../../../../components/ui/label";
import { FaFileZipper, FaPlus } from "react-icons/fa6";
import { useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Trash2 } from "lucide-react";
import textImg from "../../../../../assets/images/text-img.png";
import { Checkbox } from "../../../../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import {
  useGetConfigs,
  useGetPorts,
  useGetScanners,
} from "../../../../../helpers/queries/targets/useGetTargets";
import { useGetAllCredentials } from "../../../../../helpers/queries/credentials/useGetCredentials";
import { useNavigate } from "react-router-dom";
import TagsInput from "../../../../../components/TagsInput";
import { exportSampleFile } from "../../../../../helpers/api";

const AddTargetModal = ({
  triggerLabel = "Add a target",
  modalTitle = "Add New Target",
  targetData,
  is_asset_group = false,
  icon = <FaPlus size={16} />,
}) => {
  // const [portType, setPortType] = useState("");

  const { data: ports } = useGetPorts();
  const { data: scanners } = useGetScanners();
  const { data: configs } = useGetConfigs();
  let { data: credentials } = useGetAllCredentials();

  credentials = credentials?.data ?? [];

  const portOptions = (ports ?? [])?.map((item) => ({
    value: item["@id"],
    label: item.name,
  }));

  const selectedPort = portOptions?.find(
    (type) => type.label === "All IANA assigned TCP and UDP"
  );

  const scannerOptions = (scanners ?? [])?.map((item) => ({
    value: item["@id"],
    label: item.name,
  }));

  const configOptions = (configs ?? [])?.map((item) => ({
    value: item["@id"],
    label: item.name,
  }));

  const selectedConfig = configOptions?.find(
    (type) => type.label === "Full and fast"
  );

  const [open, setOpen] = useState(false);

  const [type, setType] = useState("manual");

  const [startScan, setStartScan] = useState(false);

  const getFormSchema = (type) => {
    const base = {
      name: z.string().min(1, "Name cannot be empty"),
      asset_type: z.string().min(1, "Please select a type"),
      target: z.string().optional(),
      assetListFile: z
        .union([z.instanceof(File), z.string().nullable(), z.null()])
        .optional(),
      description: z.string().optional(),
      autoStartScan: z.boolean().optional(),
    };

    const scanFields = {
      // ports: z.string().min(1, "Please select a port"),
      scanners: z.string().min(1, "Please select a scanner"),
      // configs: z.string().min(1, "Please select a config"),
      credential: z.string().optional(),
    };

    const schema = startScan
      ? z.object({ ...base, ...scanFields })
      : z.object(base);

    return schema.refine(
      (data) => {
        if (type === "manual") {
          return data.target?.trim();
        } else {
          return data.assetListFile instanceof File;
        }
      },
      {
        message: "This field is required",
        path: [type === "manual" ? "target" : "assetListFile"],
      }
    );
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const formSchema = useMemo(() => getFormSchema(type), [type, startScan]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      target: "",
      asset_type: "",
    },
  });

  useEffect(() => {
    if (targetData) {
      const randomSuffix = Math.floor(1000 + Math.random() * 9000); // 4-digit number
      form.reset({
        name:
          `${targetData.name} - ${targetData.target} - ${randomSuffix}` || "",
        target: targetData.target || "",
        asset_type: targetData.asset_type || "",
      });
    }
  }, [targetData, form]);

  const { control, register, setValue, watch } = form;

  const file = watch("assetListFile");
  const assetType = watch("asset_type");

  const removeFile = () => {
    setValue("assetListFile", null);
  };

  const { getRootProps, getInputProps } = useDropzone({
  accept: { "text/csv": [".csv"] },
  multiple: false,
  onDrop: (files) => {
    if (files.length > 0) {
      setValue("assetListFile", files[0], { shouldValidate: true });
    }
  },
});

  const filteredScannerOptions =
    assetType === "web"
      ? scannerOptions?.filter((opt) => opt.label === "TuringTower Web Scanner")
      : assetType === "infrastructure"
      ? scannerOptions?.filter(
          (opt) =>
            opt.label === "TuringTower Default" ||
            opt.label === "TuringTower Agent"
        )
      : [];

  const [loadingExport, setLoadingExport] = useState(false);

  const handleDownloadSampleFile = async () => {
    setLoadingExport(true);
    try {
      const blob = await exportSampleFile();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sample_inventory.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download file", error);
      toast.error("Download failed");
    } finally {
      setLoadingExport(false);
    }
  };

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate: createAsset, isPending: assetLoading } = useMutation({
    mutationFn: createTarget,
    onSuccess: (data) => {
      toast(data?.message);
      queryClient.invalidateQueries({
        queryKey: ["targets"],
      });
      queryClient.invalidateQueries({ queryKey: ["asset_groups"] });
      form.reset();
      setOpen(false);
      navigate("/dashboard/assets");
    },
    onError: (data) => {
      toast.error(data.message);
    },
  });

  const { mutate: scanAsset, isPending: scanLoading } = useMutation({
    mutationFn: createScan,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["scans"],
      });
      toast(data?.message);
      form.reset();
      setOpen(false);
      navigate("/dashboard/scan-history");
    },
    onError: (data) => {
      toast.error(data.message);
    },
  });

  const onSubmit = (data) => {
    if (startScan) {
      createAsset(
        { is_asset_group: is_asset_group, ...data },
        {
          onSuccess: (response) => {
            const id = response?.target_id;

            if (!id) {
              toast("Failed to get target ID.");
              return;
            }
            // Optional delay if needed
            setTimeout(() => {
              scanAsset({
                port_type: selectedPort?.label,
                ports: selectedPort?.value,
                config_id: selectedConfig?.value,
                target_id: id, // ✅ Use the correct ID directly from response
                credential_id: Number(data?.credential),
                autoStartScan: data?.autoStartScan,
                scanner_id: data?.scanners,
              });
            }, 3000);
          },
          onError: (error) => {
            toast(error.message);
          },
        }
      );
    } else {
      createAsset({ is_asset_group: is_asset_group, ...data }); // No scan needed
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="w-fit px-2 rounded-lg" onClick={() => setOpen(true)}>
          {icon}
          <span className="font-medium pr-2">{triggerLabel}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="">
        <DialogHeader className="text-left">
          <DialogTitle className="text-md text-semibold text-black">
            {modalTitle}
          </DialogTitle>
          <DialogDescription>
            Provide the website or IP address you'd like to monitor
          </DialogDescription>
        </DialogHeader>
        <hr />
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 mt-1"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter name" {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="asset_type"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Asset Type</FormLabel>
                  <Select
                    onValueChange={
                      field.onChange // Update the form field value
                    }
                    value={field?.value}
                  >
                    <FormControl>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select asset type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="web">Web Asset</SelectItem>
                      <SelectItem value="infrastructure">
                        Infrastructure Asset
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col">
              <label
                htmlFor="inputType"
                className="text-sm font-medium text-neutral1000 mb-3"
              >
                Host Input Type
              </label>
              <RadioGroup
                // defaultValue="manual"
                value={type}
                className="flex items-center gap-12 mb-2"
                onValueChange={(value) => setType(value)}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem
                    value="manual"
                    id="r1"
                    disabled={!assetType}
                  />
                  <Label htmlFor="r1" className="m-0">
                    Manual Entry
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem
                    value="upload"
                    id="r2"
                    disabled={!assetType}
                  />
                  <Label htmlFor="r2" className="m-0">
                    File Upload
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {type === "manual" && (
              <>
                <FormField
                  control={form.control}
                  name="target"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Domain(s) or IP Address(es)</FormLabel>
                      <TagsInput
                        // value={field.value || []}
                        // onChange={field.onChange}
                        value={field.value ? field.value.split(",") : []} // show as array
                        onChange={(tags) => field.onChange(tags.join(","))} // save as string
                        placeholder={
                          assetType === "web"
                            ? "https://example.com, http://example.com, https://example.com/login.php"
                            : "E.g 192.168.8.1, 192.168.8.2, target.com"
                        }
                        disabled={!assetType}
                      />
                      <FormMessage /> {/* Will now show the message */}
                    </FormItem>
                  )}
                />
              </>
            )}

            {type === "upload" && (
              <>
                <div className="w-full">
                  <Button
                    className="text-blue-500 p-0 mb-3"
                    variant={"ghost"}
                    loading={loadingExport}
                    onClick={handleDownloadSampleFile}
                  >
                    <FaFileZipper /> Download sample file
                  </Button>

                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload a .csv file containing your IPs/domains.{" "}
                    <span className="text-red-500">*</span>
                  </label>

                  <div className="border border-[#3636361A] rounded-lg p-6">
                    {!file ? (
                      <FormField
                        control={control}
                        name="assetListFile"
                        render={() => (
                          <>
                            <FormItem>
                              <div
                                {...getRootProps()}
                                className="flex flex-col items-center justify-center  cursor-pointer text-center bg-white hover:border-gray-400 transition-colors"
                              >
                                <input
                                  {...getInputProps()}
                                  // onChange={(e) => {
                                  //   onChange(e.target.files?.[0]); // correctly set the file object
                                  // }}
                                  // {...rest}
                                />

                                <FiUploadCloud className="text-2xl mb-2 text-[#B7B7B9]" />

                                <p className="text-sm text-neutral900 font-medium mt-3">
                                  Choose a file or drag & drop it here
                                </p>
                                <p className="text-xs text-neutral800 mt-1 mb-3">
                                  Please upload a .csv file
                                </p>
                                {/* <p className="text-xs text-neutral800 mt-1 ">
                                  Each entry should be separated by a comma.
                                </p>
                                <p className="text-xs text-neutral800 mt-1 mb-4">
                                  {assetType === "web"
                                    ? "https://example.com, http://example.com, https://example.com/login.php"
                                    : "E.g 192.168.8.1, 192.168.8.2, target.com"}
                                </p> */}

                                <p className="text-neutral900 px-4 py-2 text-sm font-medium border rounded-lg ">
                                  Browse file
                                </p>
                              </div>
                            </FormItem>
                            <FormMessage />
                          </>
                        )}
                      />
                    ) : (
                      <div className="flex items-center justify-between bg-white">
                        <div className="flex items-center gap-3">
                          <img
                            src={textImg}
                            alt="textImg"
                            className="w-[32px]"
                          />
                          <div>
                            <p className="font-medium text-sm text-gray-800">
                              {file.name}
                            </p>
                          </div>
                        </div>

                        <Trash2
                          onClick={removeFile}
                          className="text-gray-500 hover:text-red-600 transition"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="flex flex-col">
              <label
                htmlFor="description"
                className="text-sm  font-medium text-neutral1000"
              >
                Description
              </label>
              <Textarea
                {...register("description")}
                placeholder="Add asset description"
                disabled={!assetType}
              ></Textarea>
            </div>

            <FormField
              control={form.control}
              name="autoStartScan"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center  space-x-3">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(value) => {
                        const isChecked = value === true;
                        field.onChange(isChecked);
                        setStartScan(isChecked); // keep local state if needed
                      }}
                      disabled={!assetType}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Start Scan Automatically?</FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            {startScan && (
              <>
                {/* <FormField
                  control={form.control}
                  name="ports"
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>Ports</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value); // Update the form field value
                          const selectedLabel = portOptions.find(
                            (option) => option.value === value
                          )?.label;
                          setPortType(selectedLabel); // Update the selected port label
                        }}
                        value={field?.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select a port" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {portOptions?.map((type) =>
                            type.label === "All IANA assigned TCP and UDP" ? (
                              <SelectItem key={type.label} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ) : null
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                /> */}

                <FormField
                  control={form.control}
                  name="scanners"
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>Scanners</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field?.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select a scanner" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {filteredScannerOptions?.map((type) => (
                            <SelectItem key={type.label} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* <FormField
                  control={form.control}
                  name="configs"
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>Configs</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field?.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select config" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {configOptions?.map((type) =>
                            type.label === "Full and fast" ? (
                              <SelectItem key={type.label} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ) : null
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                /> */}

                <FormField
                  control={form.control}
                  name="credential"
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>Credential (Optional)</FormLabel>
                      <Select
                        onValueChange={(value) =>
                          field.onChange(value === "none" ? "" : value)
                        }
                        value={field.value || "none"}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select credential" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">No option</SelectItem>
                          {credentials?.map((type) => (
                            <SelectItem key={type.id} value={String(type.id)}>
                              {type.name ?? "invalid"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="submit"
                className="px-4 py-2 font-medium text-white"
                loading={startScan ? assetLoading || scanLoading : assetLoading}
              >
                {startScan
                  ? "Create and scan target"
                  : is_asset_group
                  ? "Create asset group"
                  : "Create target"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddTargetModal;
